import PerformanceTimeline from "./PerformanceTimeline";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import {
  CHART_TOOLTIP_GUTTER,
  placeChartTooltip,
  resolveTooltipViewport,
} from "./chartTooltipPlacement";

/* The chart's own styles travel with the chart, so every surface that renders
   it renders it looking the same. */
import "./DeliveryChart.css";

/*
 * The canonical Delivery-and-efficiency chart — ONE implementation, every
 * surface.
 *
 * Quantum (/cross-platform-analytics), authenticated Cortex Platform
 * Analytics, and the public report microsite all render THIS component. There
 * is no second chart architecture: the surfaces differ only in the model they
 * hand it, never in how that model is drawn.
 *
 * The model is built by deliveryChartModel.js in one of two governed states:
 *
 *   columnAxis: "time"     — a certified daily/weekly series. Columns are time
 *                            buckets, stacked by platform, with the plan step,
 *                            the modeled projection zone, and the GA4 site-visit
 *                            overlay on its own right-hand scale.
 *   columnAxis: "platform" — window totals only. Columns are the PLATFORM SET,
 *                            one certified value each, printed above the column.
 *
 * The second state is not a fallback visualization. It is this chart with the
 * series it does not have left out: same gridlines, same axis labels, same
 * rounded-top segments in the same canonical palette, same hover column, same
 * tooltip, same legend, same measured geometry. When the daily reporting
 * contract certifies for a surface, that surface starts passing a time-axis
 * model and the panel becomes the full visualization without a redesign.
 *
 * This component is pure presentation. It never derives a metric, never fills a
 * gap, and never formats a number itself — every value and every label arrives
 * resolved on the model, so the surfaces cannot drift in what a number means.
 */

/* Column geometry per axis. Time buckets are many and narrow; a platform set is
   a handful of columns and would read as a bug at 34px. */
const BAR_GEOMETRY = Object.freeze({
  time: { minWidth: 3, maxWidth: 34, slotFraction: 0.62 },
  platform: { minWidth: 10, maxWidthNarrow: 64, maxWidth: 120, slotFraction: 0.5 },
});

function classNames(...values) {
  return values.filter(Boolean).join(" ");
}

/* The one breakpoint at which the reading stops floating and becomes a block
   beneath the plot. Mirrored by the `max-width: 768px` rule in
   DeliveryChart.css; the floating variant is a class, so the two can never
   disagree about which presentation is on screen. */
const FLOATING_TOOLTIP_QUERY = "(max-width: 768px)";

function canFloatTooltip() {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (typeof window.matchMedia !== "function") return true;
  return !window.matchMedia(FLOATING_TOOLTIP_QUERY).matches;
}

function samePlacement(a, b) {
  if (!a || !b) return false;
  return (
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.maxHeight - b.maxHeight) < 0.5 &&
    a.placement === b.placement &&
    a.constrained === b.constrained
  );
}

function useMeasuredWidth(fallback = 1120) {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const apply = () => {
      const measured = node.getBoundingClientRect().width;
      if (measured > 0) setWidth(measured);
    };
    apply();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", apply);
      return () => window.removeEventListener("resize", apply);
    }
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

function roundedTopPath(x, y, width, height, radius) {
  const r = Math.max(0, Math.min(radius, width / 2, height));
  return [
    `M ${x} ${y + height}`,
    `L ${x} ${y + r}`,
    `Q ${x} ${y} ${x + r} ${y}`,
    `L ${x + width - r} ${y}`,
    `Q ${x + width} ${y} ${x + width} ${y + r}`,
    `L ${x + width} ${y + height}`,
    "Z",
  ].join(" ");
}

// One owner for the existing rounded delivery column, also used beneath the
// platform timeline's added lines. Styling and geometry stay canonical.
export function DeliveryChartBar({ x, y, width, height, radius = 4, ...props }) {
  return <path className="cpx-chart-segment" d={roundedTopPath(x, y, width, height, radius)} {...props} />;
}

function niceCeil(value) {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

/* The right-hand site-visit axis prints three marks. Shared between the scale
   and the gutter that has to hold its labels. */
const SITE_VISIT_TICK_FRACTIONS = [0, 0.5, 1];

/* Axis labels are drawn by the SVG, which lays out no text for us, so a gutter
   is sized from the widest label its scale will print. 11px AquireLight digits
   run about 6.4px wide; commas, periods and a currency sign are narrower, so
   this over-reserves a little rather than ever letting a label cross into the
   plot. The tick text sits 10px inside the gutter's edge. */
const AXIS_LABEL_CHAR_WIDTH = 6.6;
const axisGutterFor = (labels, floor) => {
  const longest = Math.max(0, ...labels.map((label) => String(label ?? "").length));
  return Math.max(floor, Math.ceil(longest * AXIS_LABEL_CHAR_WIDTH) + 16);
};

/*
 * The chart legend. Rendered next to the plot on every surface from the same
 * model, so a platform swatch, the plan step and the modeled projection are
 * named identically wherever they appear.
 */
export function DeliveryChartLegend({ model, mode = "efficiency" }) {
  if (!model || model.performance) return null;
  const efficiency = model.efficiency;
  const showEfficiency = mode === "efficiency" && Boolean(efficiency?.comparable);
  return (
    <div className="cpx-legend" aria-hidden="false">
      {(model.legend || []).map((entry) => (
        <span key={entry.key} className="cpx-legend-item">
          <span
            className="cpx-swatch"
            style={{ backgroundColor: entry.color }}
            aria-hidden="true"
          />
          {entry.label}
          {/*
            * This platform's governed efficiency reading, named by the metric
            * that actually describes it — CTR on click inventory, VVR on video
            * inventory. It sits on the platform's own legend entry because it
            * belongs to that platform and to no other, and because a reader
            * should not have to hover a chart to find out how it performed.
            *
            * It appears HERE and nowhere else. A model that carries legend
            * rates carries no efficiency series, so there is no second copy to
            * disagree with this one.
            */}
          {entry.rate ? (
            <span className="cpx-legend-rate cpx-numeric">
              {entry.rate.value}
              <span className="cpx-legend-rate-label">{entry.rate.label}</span>
            </span>
          ) : null}
        </span>
      ))}
      {showEfficiency && (
        <span className="cpx-legend-item">
          {/* The swatch states how the series is drawn: a rule for a trend, a
              dot for independent per-column readings. */}
          <span
            className={
              efficiency.plotAs === "markers"
                ? "cpx-legend-dot cpx-legend-dot--eff"
                : "cpx-legend-line cpx-legend-line--eff"
            }
            aria-hidden="true"
          />
          {efficiency.label}
        </span>
      )}
      {model.plan?.available && (
        <span className="cpx-legend-item">
          <span className="cpx-legend-line cpx-legend-line--plan" aria-hidden="true" />
          {model.plan.label}
        </span>
      )}
      {model.projection && (
        <span className="cpx-legend-item">
          <span className="cpx-legend-line cpx-legend-line--projection" aria-hidden="true" />
          Projected (modeled)
        </span>
      )}
      {model.siteVisits?.available && (
        <span className="cpx-legend-item">
          <span className="cpx-legend-line cpx-legend-line--visits" aria-hidden="true" />
          {model.siteVisits?.label || "Site Visits · GA4"}
        </span>
      )}
    </div>
  );
}

export default function DeliveryChart(props) {
  return props.model?.performance
    ? <PerformanceTimeline model={props.model.performance} BarComponent={DeliveryChartBar} />
    : <LegacyDeliveryChart {...props} />;
}

function LegacyDeliveryChart({
  model,
  mode = "efficiency",
  highlightPlatform = null,
  onTogglePlatform = null,
  focusPlatform = null,
  compact = false,
  /* "comfortable" (page default) | "mini" (dashboard card). */
  density = "comfortable",
}) {
  const [containerRef, measuredWidth] = useMeasuredWidth();
  /*
   * Hover state is owned HERE, by the chart, and released by the chart.
   *
   * It used to be set by every column and segment and released by nothing but
   * blur — so a mouse that entered one column and then left the chart entirely
   * left the tooltip pinned over whatever it happened to be covering, usually
   * the next platform's bar. The set is per-target; the release has to be for
   * the whole plot, or there is always a way out that nobody cleared.
   */
  const [hoverIndex, setHoverIndex] = useState(null);

  /*
   * The reading is drawn OUTSIDE the chart's box.
   *
   * Every surface that renders this chart drops it into a card, and cards have
   * frames: the Cortex and Quantum welcome dashboards put it inside
   * `.cortex-welcome-panel`, which carries `overflow: hidden` for its rounded
   * 28px frame and its corner washes. A tooltip that is taller than the space
   * left under it therefore had its lower rows cut off at the panel edge — the
   * platform rows the reader hovered for. No z-index reaches out of an
   * ancestor's overflow, and the frame is not the thing that should give way,
   * so the reading is portalled to the document and positioned against the
   * VIEWPORT from the hovered column's real rectangle.
   *
   * `getBoundingClientRect()` on the column is what makes that durable: it
   * already accounts for scroll, page zoom, and any scaling transform an
   * ancestor applies, so the reading cannot drift away from the point it
   * describes the way hand-computed coordinates do.
   */
  const tooltipRef = useRef(null);
  const columnAnchorsRef = useRef([]);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  /*
   * A phone gets the in-flow treatment instead. There is nowhere on a 390px
   * screen for a floating panel to go that is not on top of the plot it
   * describes, so below 768px the reading stays a block beneath the chart —
   * the behaviour that already shipped, kept rather than replaced.
   */
  const [floating, setFloating] = useState(canFloatTooltip);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const query = window.matchMedia(FLOATING_TOOLTIP_QUERY);
    /*
     * ONE function decides, and it is the same one the initial state uses.
     *
     * This used to be `setFloating(query.matches)` — inverted, because
     * `matches` is true BELOW the breakpoint, which is exactly when the
     * reading must NOT float. The initial state was right and the effect
     * immediately overwrote it, so every desktop fell back to the in-flow
     * reading and was clipped by the card again. Re-deriving through
     * `canFloatTooltip()` removes the second copy of the rule that could
     * disagree with the first.
     */
    const apply = () => setFloating(canFloatTooltip());
    apply();
    /* Safari below 14 has only the deprecated listener pair. */
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", apply);
      return () => query.removeEventListener("change", apply);
    }
    query.addListener(apply);
    return () => query.removeListener(apply);
  }, []);

  /*
   * Leaving the plot releases the reading — unless the pointer left INTO the
   * reading itself. A constrained tooltip is scrollable, and a panel that
   * vanishes the moment you reach for its scrollbar is not accessible content.
   */
  const clearHover = useCallback((event) => {
    const related = event && event.relatedTarget;
    const tooltipNode = tooltipRef.current;
    if (tooltipNode && related instanceof Node && tooltipNode.contains(related)) return;
    setHoverIndex(null);
  }, []);

  /*
   * The two exits a pointer-leave cannot cover.
   *
   * Touch never "leaves": a tap sets hover and the finger lifts, so without an
   * explicit dismissal the tooltip stays until the next tap on a column. A tap
   * anywhere outside the chart is that dismissal. Escape is the keyboard
   * equivalent for a reader who focused a column and wants the panel gone
   * without tabbing away from it.
   *
   * Both are real events, not timers: nothing here guesses when a reader is
   * finished looking.
   */
  useEffect(() => {
    if (hoverIndex == null) return undefined;
    const dismissOutside = (event) => {
      const node = containerRef.current;
      const target = event.target;
      if (!(target instanceof Node)) {
        setHoverIndex(null);
        return;
      }
      if (node && node.contains(target)) return;
      /* The reading is portalled, so "inside the chart" is two subtrees now.
         Without this, reaching for a constrained tooltip's scrollbar counts as
         a tap outside and dismisses the thing being reached for. */
      const tooltipNode = tooltipRef.current;
      if (tooltipNode && tooltipNode.contains(target)) return;
      setHoverIndex(null);
    };
    const dismissOnEscape = (event) => {
      if (event.key === "Escape") setHoverIndex(null);
    };
    document.addEventListener("pointerdown", dismissOutside, true);
    document.addEventListener("keydown", dismissOnEscape);
    return () => {
      document.removeEventListener("pointerdown", dismissOutside, true);
      document.removeEventListener("keydown", dismissOnEscape);
    };
  }, [hoverIndex, containerRef]);

  /*
   * Placing the reading, and keeping it placed.
   *
   * Measured from the live rectangles rather than from anything this component
   * believes about the page, so scrolling, resizing, browser zoom and a scaled
   * dashboard all resolve correctly without any of them being special-cased.
   * The listeners are capturing so an ancestor's own scroll container moves the
   * reading too, not just the document.
   */
  useLayoutEffect(() => {
    if (!floating || hoverIndex == null) {
      setTooltipPosition(null);
      return undefined;
    }
    const anchor = columnAnchorsRef.current[hoverIndex];
    const node = tooltipRef.current;
    if (!anchor || !node || typeof anchor.getBoundingClientRect !== "function") {
      return undefined;
    }

    const reposition = () => {
      const rect = node.getBoundingClientRect();
      const next = placeChartTooltip({
        anchor: anchor.getBoundingClientRect(),
        tooltip: {
          width: rect.width,
          /* `scrollHeight` is the height the rows WANT. Once a constrained
             reading is capped, `rect.height` reports the cap, so measuring
             only that would latch the constraint on and never release it when
             the viewport grows back. */
          height: Math.max(rect.height, node.scrollHeight || 0),
        },
        viewport: resolveTooltipViewport(window),
      });
      setTooltipPosition((current) => (samePlacement(current, next) ? current : next));
    };

    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    const visual = window.visualViewport;
    if (visual) {
      visual.addEventListener("resize", reposition);
      visual.addEventListener("scroll", reposition);
    }
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      if (visual) {
        visual.removeEventListener("resize", reposition);
        visual.removeEventListener("scroll", reposition);
      }
    };
  }, [floating, hoverIndex, model]);

  const columns = model?.columns || [];
  const columnAxis = model?.columnAxis === "platform" ? "platform" : "time";
  const formatVolume = model?.formatVolume || ((value) => String(value));
  /* Plan is money and site visits are people; neither is necessarily the
     selected volume metric, so each carries its own formatter. */
  const formatPlan = model?.formatPlan || formatVolume;
  const formatSiteVisits = model?.formatSiteVisits || formatVolume;
  const volumeLabel = model?.volume?.label || "Metric";
  const efficiency = model?.efficiency || null;
  /* Set by the model, not inferred from the axis: a caller can hand a platform
     axis without a rate at all, and the model is the thing that knows whether
     what it put in `series` is a trend or a set of independent readings. */
  const plotEfficiencyAsMarkers = efficiency?.plotAs === "markers";
  const indexed = model?.indexed || [];
  const projection = model?.projection || null;
  const showTotals = model?.showTotals !== false;

  /* `mini` needs to be known before the width floor: a dashboard card can be
     narrower than a page panel's 320px minimum, and a floor above the actual
     container is what makes a chart draw past its own box. */
  const mini = density === "mini";
  const width = Math.max(mini ? 240 : 320, measuredWidth);
  /* Deliberate narrow-viewport treatment: reclaim the projection zone and
     axis gutters so the delivered bars keep readable width on phones. */
  const narrow = width < 560;
  const showProjection = Boolean(projection) && !narrow;
  /* `mini` is the dashboard-card tier. The welcome panel gives this chart a
     ~180-244px card that also has to hold a header, a legend and a summary
     sentence, so the plot gets roughly a third of the page chart's height.
     (The card used to spend two of these rows on a dashed reporting-mode
     notice; the axis, legend and summary already state that truth, so the
     notice's space now belongs to the plot.) It is a size, not a different
     chart: same scales, same segments, same palette, same panels — only the
     tick set thins out, because 5 gridlines inside 120px is noise rather
     than a reading aid. */
  const barPanelHeight = mini ? 100 : compact || narrow ? 220 : 300;
  const linePanelHeight = mini ? 52 : compact || narrow ? 96 : 120;
  const siteVisitsRequested = Boolean(model?.siteVisits?.available);
  /* The right axis needs a gutter of its own. On a phone it is dropped and the
     labels go with it — the SERIES and its tooltip value stay, because the
     value is the point and the axis is only a reading aid.

     `mini` is narrow by width but is not a phone: it is the dashboard card,
     which draws its own labels a step down in size. Two scales in one plot need
     to be readable as two, so the card gets a TIGHTER gutter rather than none —
     an amber point floating against a spend axis it is not read on is the one
     thing this overlay must never look like. The gutter comes out of the
     right-hand margin the card already reserves, so the card's box, its plot
     height and its column geometry are unchanged. */
  const siteVisitAxisGutterFloor = !siteVisitsRequested ? 0 : mini ? 30 : narrow ? 0 : 44;
  /* One derivation, so the labels can never be drawn into space no gutter was
     reserved for — or the space reserved and then left empty. */
  const showSiteVisitAxis = siteVisitAxisGutterFloor > 0;
  /* The rate markers read against a right-hand axis, which needs a gutter of
     its own so the tick labels never sit on top of the last column. */
  const efficiencyAxisGutter =
    plotEfficiencyAsMarkers && mode === "efficiency" ? 44 : 0;

  /* Scale extents come before the gutters because the gutters are sized from
     the labels each scale will print. */
  const columnTotal = (column) =>
    (column.segments || []).reduce(
      (sum, segment) => sum + Math.max(0, segment.value || 0),
      0
    );
  const maxStack = Math.max(
    1e-9,
    ...columns.map(columnTotal),
    ...columns.map((column) => column.planMedia || 0)
  );
  const yMax = niceCeil(maxStack * 1.05);
  const yTicks = (narrow || mini ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1]).map(
    (fraction) => fraction * yMax
  );
  /* Site visits get their OWN scale and their own axis. Visits and spend share
     no unit, so forcing them onto one axis would either flatten the line into
     the baseline or invent a relationship by making the curves track. The
     right-hand axis is what keeps this a correlation view rather than an
     implied equivalence. */
  const siteVisitValues = columns
    .map((column) => column.siteVisits)
    .filter((value) => value != null);
  const showSiteVisits = siteVisitsRequested && siteVisitValues.length > 0;
  const visitMax = showSiteVisits ? niceCeil(Math.max(...siteVisitValues) * 1.15) : 1;
  const visitTicks = SITE_VISIT_TICK_FRACTIONS.map((fraction) => fraction * visitMax);

  /* Axis labels state the scale in full — "$15,000", "28,000,000" — so the
     gutter that holds them is sized from the widest label the scale will
     print rather than from a fixed width that fit "15K". The floor is the
     tier's approved gutter, so a scale whose labels are short draws exactly
     as it did. */
  const marginLeft = axisGutterFor(
    yTicks.map((tick) => formatVolume(tick, { compact: true, axis: true })),
    mini ? 44 : narrow ? 46 : 62
  );
  const siteVisitAxisGutter =
    showSiteVisitAxis && showSiteVisits
      ? axisGutterFor(
          visitTicks.map((tick) => formatSiteVisits(tick, { compact: true, axis: true })),
          siteVisitAxisGutterFloor
        )
      : siteVisitAxisGutterFloor;
  const marginRight =
    (showProjection ? 150 : narrow ? 12 : 20) +
    siteVisitAxisGutter +
    efficiencyAxisGutter;
  /* Value labels sit above their column, so the plot needs headroom the time
     axis does not. */
  const marginTop = columns.some((column) => column.valueLabel) ? 22 : 0;
  const baseline = marginTop + barPanelHeight;
  const plotWidth = Math.max(60, width - marginLeft - marginRight);
  const columnCount = columns.length;
  const slot = columnCount > 0 ? plotWidth / columnCount : plotWidth;
  const geometry = BAR_GEOMETRY[columnAxis];
  const barWidth = Math.max(
    geometry.minWidth,
    Math.min(
      columnAxis === "platform" && narrow ? geometry.maxWidthNarrow : geometry.maxWidth,
      slot * geometry.slotFraction
    )
  );

  const yScale = (value) => baseline - (value / yMax) * barPanelHeight;

  const indexedValues = indexed.flatMap((entry) =>
    (entry.points || []).filter((value) => value != null)
  );
  const indexedMin = indexedValues.length
    ? Math.min(80, Math.floor(Math.min(...indexedValues) / 10) * 10)
    : 80;
  const indexedMax = indexedValues.length
    ? Math.max(120, niceCeil(Math.max(...indexedValues)))
    : 120;

  const efficiencySeries = efficiency?.series || [];
  const priorEfficiencySeries = efficiency?.priorSeries || [];
  const effValues = efficiencySeries.filter((value) => value != null);
  const priorEffValues = priorEfficiencySeries.filter((value) => value != null);
  const effMax = Math.max(1e-9, ...effValues, ...priorEffValues) * 1.15;
  const effMin =
    Math.min(
      ...(effValues.length ? effValues : [0]),
      ...(priorEffValues.length ? priorEffValues : [Infinity])
    ) * 0.85;
  const effScale = (value) =>
    linePanelHeight - ((value - effMin) / Math.max(1e-9, effMax - effMin)) * linePanelHeight;

  const visitScale = (value) => baseline - (value / visitMax) * barPanelHeight;

  const xCenter = (index) => marginLeft + index * slot + slot / 2;

  const labelEvery = Math.max(
    1,
    Math.ceil(columnCount / Math.max(4, Math.floor(plotWidth / 74)))
  );

  const hoverColumn = hoverIndex != null ? columns[hoverIndex] : null;

  /* Deliberately NOT linePath: that helper skips nulls and keeps drawing, which
     bridges a gap with a straight segment and reads as interpolated data. A day
     GA4 never reported must leave a hole, so each run of observations becomes
     its own subpath. */
  const segmentedLinePath = (values, valueScale) => {
    let d = "";
    let pendingMove = true;
    values.forEach((value, index) => {
      if (value == null) {
        pendingMove = true;
        return;
      }
      d += `${pendingMove ? "M" : "L"} ${xCenter(index).toFixed(1)} ${valueScale(value).toFixed(1)} `;
      pendingMove = false;
    });
    return d.trim();
  };

  const linePath = (values, valueScale) => {
    let d = "";
    values.forEach((value, index) => {
      if (value == null) return;
      const command = d ? "L" : "M";
      d += `${command} ${xCenter(index).toFixed(1)} ${valueScale(value).toFixed(1)} `;
    });
    return d.trim();
  };

  const showBars = mode !== "indexed";
  /* Markers ride ON the bar panel against a right-hand axis instead of taking
     a panel of their own. A trend gets its own panel because its shape is the
     reading and it needs the room; a marker's reading is "this column's rate",
     so it belongs above the column it describes — a detached panel would make
     you eye-trace between two rows to pair them up, and would cost a second
     axis band the dashboard card has no height for. */
  const showEfficiencyOverlay =
    mode === "efficiency" && Boolean(efficiency?.comparable) && plotEfficiencyAsMarkers;
  const showEfficiencyPanel =
    mode === "efficiency" && Boolean(efficiency?.comparable) && !plotEfficiencyAsMarkers;
  /* The markers get the top of the bar panel so they never sit inside the
     columns, where a cyan dot on a coloured bar reads as part of the bar. */
  const overlayTop = marginTop + 4;
  const overlayBottom = marginTop + barPanelHeight * 0.42;
  const overlayScale = (value) =>
    overlayBottom -
    ((value - effMin) / Math.max(1e-9, effMax - effMin)) * (overlayBottom - overlayTop);
  const interactive = typeof onTogglePlatform === "function";

  if (!columnCount) return null;

  /* A reading taller than the viewport allows becomes SCROLLABLE, never
     cropped: every platform, campaign, total and outcome row stays reachable.
     Scrollable means it also has to accept the pointer, which is why the
     leave-handlers above bridge the gap between the plot and the reading. */
  const tooltipConstrained = floating && Boolean(tooltipPosition && tooltipPosition.constrained);
  const floatingStyle = floating
    ? {
        /* ONE home for the gutter: the placement module clamps `left`/`top`
           against it, and the stylesheet reads the same number back out for
           the floating `max-width`, so a narrow viewport can never be handed
           horizontal overflow by a tooltip wider than it is. */
        "--cpx-tooltip-gutter": `${CHART_TOOLTIP_GUTTER}px`,
        left: `${tooltipPosition ? tooltipPosition.left : 0}px`,
        top: `${tooltipPosition ? tooltipPosition.top : 0}px`,
        maxHeight: tooltipPosition ? `${tooltipPosition.maxHeight}px` : undefined,
        /* One frame is measured before it is placed. Hidden rather than
           unmounted, because a zero-size node cannot be measured at all. */
        visibility: tooltipPosition ? undefined : "hidden",
      }
    : undefined;
  /* Out of the card's `overflow: hidden`, into the document — the only place
     no ancestor frame can clip it. On a phone it stays where it is written,
     because there it is a block in the flow rather than a floating panel. */
  const renderTooltip = (element) =>
    floating && typeof document !== "undefined"
      ? createPortal(element, document.body)
      : element;

  return (
    <div
      className="cpx-chart-wrap"
      data-delivery-chart={columnAxis === "platform" ? "window-totals" : "time-series"}
      ref={containerRef}
      /* Leaving the plot releases the reading, however the pointer left it and
         whatever it was over. The tooltip itself is pointer-events:none, so it
         can never sit between the pointer and this boundary. */
      onPointerLeave={clearHover}
      onMouseLeave={clearHover}
    >
      {showBars && (
        <svg
          className="cpx-chart-svg"
          width={width}
          height={baseline + 34}
          role="img"
          aria-label={`${volumeLabel} by platform${
            columnAxis === "time" ? " over time" : ", window totals"
          }. ${model?.textSummary || ""}`}
        >
          {yTicks.map((tick) => (
            <g key={`grid-${tick}`}>
              <line
                x1={marginLeft}
                x2={width - marginRight}
                y1={yScale(tick)}
                y2={yScale(tick)}
                className={classNames(
                  "cpx-chart-gridline",
                  tick === 0 && columnAxis === "platform" && "cpx-chart-gridline--baseline"
                )}
              />
              <text
                x={marginLeft - 10}
                y={yScale(tick) + 4}
                className="cpx-chart-axis-label"
                textAnchor="end"
              >
                {formatVolume(tick, { compact: true, axis: true })}
              </text>
            </g>
          ))}

          {columns.map((column, index) => {
            const x = xCenter(index) - barWidth / 2;
            let cursorY = baseline;
            const segments = (column.segments || [])
              .map((segment) => {
                const value = Math.max(0, segment.value || 0);
                if (value <= 0) return null;
                const height = (value / yMax) * barPanelHeight;
                cursorY -= height;
                return { ...segment, value, y: cursorY, height };
              })
              .filter(Boolean);
            const topSegment = segments[segments.length - 1];
            const total = columnTotal(column);
            const focused = Boolean(column.platformKey) && focusPlatform === column.platformKey;
            const columnInteractive = interactive && Boolean(column.platformKey);
            return (
              <g key={column.key} data-platform-column={column.platformKey || undefined}>
                {segments.map((segment) => {
                  const dimmed = highlightPlatform && highlightPlatform !== segment.key;
                  const gap = segment === topSegment ? 0 : 2;
                  const drawnHeight = Math.max(0.75, segment.height - gap);
                  const isTop = segment === topSegment;
                  const commonProps = {
                    className: classNames(
                      "cpx-chart-segment",
                      dimmed && "cpx-chart-segment--dimmed"
                    ),
                    fill: segment.color,
                    onMouseEnter: () => setHoverIndex(index),
                    ...(interactive ? { onClick: () => onTogglePlatform(segment.key) } : {}),
                  };
                  return isTop ? (
                    <DeliveryChartBar key={segment.key} x={x} y={segment.y} width={barWidth} height={drawnHeight} radius={columnAxis === "platform" ? 5 : 4} {...commonProps} />
                  ) : (
                    <rect
                      key={segment.key}
                      x={x}
                      y={segment.y + gap}
                      width={barWidth}
                      height={drawnHeight}
                      {...commonProps}
                    />
                  );
                })}
                <rect
                  x={marginLeft + index * slot}
                  y={marginTop}
                  width={slot}
                  height={barPanelHeight}
                  className="cpx-chart-hover-column"
                  /* The reading's anchor. This rect is the full column slot, so
                     its live rectangle is exactly the geometry the tooltip has
                     to sit beside — including under a scaled or zoomed
                     dashboard, where a computed coordinate would drift. */
                  ref={(node) => {
                    columnAnchorsRef.current[index] = node;
                  }}
                  tabIndex={0}
                  role="button"
                  {...(columnInteractive ? { "aria-pressed": focused } : {})}
                  aria-label={
                    columnAxis === "platform"
                      ? `${column.label}: ${formatVolume(total, { compact: true })} ${volumeLabel}.${
                          columnInteractive
                            ? focused
                              ? " Focused — activate to clear."
                              : " Activate to focus this platform."
                            : ""
                        }`
                      : `${column.label}: ${formatVolume(total, {
                          compact: true,
                        })} total ${volumeLabel}`
                  }
                  onMouseEnter={() => setHoverIndex(index)}
                  onFocus={() => setHoverIndex(index)}
                  onBlur={() => setHoverIndex((current) => (current === index ? null : current))}
                  {...(columnInteractive
                    ? {
                        onClick: () => onTogglePlatform(column.platformKey),
                        onKeyDown: (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onTogglePlatform(column.platformKey);
                          }
                        },
                      }
                    : {})}
                />
                {column.valueLabel ? (
                  <text
                    x={xCenter(index)}
                    y={yScale(total) - 8}
                    className="cpx-window-column-value cpx-numeric"
                    textAnchor="middle"
                  >
                    {column.valueLabel}
                  </text>
                ) : null}
                {index % labelEvery === 0 && (
                  <text
                    x={xCenter(index)}
                    y={baseline + 22}
                    className={classNames(
                      "cpx-chart-axis-label",
                      columnAxis === "platform" && "cpx-window-column-name"
                    )}
                    textAnchor="middle"
                  >
                    {column.label}
                  </text>
                )}
              </g>
            );
          })}

          {/* Site visits · GA4 — observed website traffic on its own right-hand
              scale. Drawn before the plan/projection so those governed overlays
              stay on top, and after the bars so it reads as a response to them. */}
          {showSiteVisits && (
            <g className="cpx-chart-site-visits">
              {showSiteVisitAxis &&
                visitTicks.map((tick, tickIndex) => {
                  const fraction = SITE_VISIT_TICK_FRACTIONS[tickIndex];
                  return (
                    <text
                      key={`visit-tick-${fraction}`}
                      x={width - marginRight + siteVisitAxisGutter - 6}
                      y={visitScale(tick) + 4}
                      className="cpx-chart-axis-label cpx-chart-axis-label--right"
                      textAnchor="end"
                    >
                      {formatSiteVisits(tick, { compact: true, axis: true })}
                    </text>
                  );
                })}
              <path
                className="cpx-chart-site-visits-line"
                d={segmentedLinePath(
                  columns.map((column) => column.siteVisits),
                  visitScale
                )}
              />
              {columns.map((column, index) =>
                column.siteVisits == null ? null : (
                  <circle
                    key={`visit-dot-${column.key}`}
                    className="cpx-chart-site-visits-dot"
                    cx={xCenter(index)}
                    cy={visitScale(column.siteVisits)}
                    r={narrow ? 1.6 : 2.1}
                  />
                )
              )}
            </g>
          )}

          {/* Plan line — stepped, net media basis. */}
          {columns.some((column) => column.planMedia > 0) && (
            <path
              className="cpx-chart-plan-line"
              d={columns
                .map((column, index) => {
                  const y = yScale(column.planMedia || 0).toFixed(1);
                  const x0 = (marginLeft + index * slot).toFixed(1);
                  const x1 = (marginLeft + (index + 1) * slot).toFixed(1);
                  return `${index === 0 ? "M" : "L"} ${x0} ${y} L ${x1} ${y}`;
                })
                .join(" ")}
            />
          )}

          {/* Projection — modeled run-rate, clearly separated + labeled. */}
          {showProjection && (
            <g className="cpx-chart-projection">
              <rect
                x={width - marginRight + 6}
                y={marginTop}
                width={marginRight - 10}
                height={barPanelHeight}
                className="cpx-chart-projection-zone"
              />
              <line
                x1={width - marginRight + 10}
                x2={width - 14}
                y1={yScale(projection.dailyValue)}
                y2={yScale(projection.dailyValue)}
                className="cpx-chart-projection-line"
              />
              <text x={width - marginRight + 12} y={marginTop + 16} className="cpx-chart-axis-label">
                Projected
              </text>
              <text
                x={width - marginRight + 12}
                y={yScale(projection.dailyValue) - 8}
                className="cpx-chart-projection-value"
              >
                {formatVolume(projection.dailyValue, { compact: true })}/day
              </text>
            </g>
          )}

          {/* Certified per-platform rate, as points and only points. */}
          {showEfficiencyOverlay && (
            <g className="cpx-chart-eff-markers">
              {[effMin, effMax].map((tick, tickIndex) => (
                <text
                  key={`eff-tick-${tickIndex}`}
                  x={width - marginRight + 8}
                  y={overlayScale(tick) + 4}
                  className="cpx-chart-axis-label cpx-chart-axis-label--eff"
                >
                  {efficiency.format(tick)}
                </text>
              ))}
              {efficiencySeries.map((value, index) =>
                value == null ? null : (
                  <g key={`eff-marker-${columns[index]?.key || index}`}>
                    <circle
                      cx={xCenter(index)}
                      cy={overlayScale(value)}
                      r={3.5}
                      className="cpx-chart-eff-endpoint"
                    />
                    <title>{`${columns[index]?.label || ""} ${
                      efficiency.label
                    }: ${efficiency.format(value)}`}</title>
                  </g>
                )
              )}
            </g>
          )}
        </svg>
      )}

      {mode === "indexed" && (
        <svg
          className="cpx-chart-svg"
          width={width}
          height={baseline + 34}
          role="img"
          aria-label={`Indexed comparison, baseline 100. ${model?.textSummary || ""}`}
        >
          {[indexedMin, 100, indexedMax].map((tick) => (
            <g key={`itick-${tick}`}>
              <line
                x1={marginLeft}
                x2={width - marginRight}
                y1={baseline - ((tick - indexedMin) / Math.max(1, indexedMax - indexedMin)) * barPanelHeight}
                y2={baseline - ((tick - indexedMin) / Math.max(1, indexedMax - indexedMin)) * barPanelHeight}
                className={classNames("cpx-chart-gridline", tick === 100 && "cpx-chart-gridline--baseline")}
              />
              <text
                x={marginLeft - 10}
                y={
                  baseline -
                  ((tick - indexedMin) / Math.max(1, indexedMax - indexedMin)) * barPanelHeight +
                  4
                }
                className="cpx-chart-axis-label"
                textAnchor="end"
              >
                {Math.round(tick)}
              </text>
            </g>
          ))}
          {(() => {
            const scale = (value) =>
              baseline - ((value - indexedMin) / Math.max(1, indexedMax - indexedMin)) * barPanelHeight;
            const endpoints = indexed
              .map((entry) => {
                const points = entry.points || [];
                const lastIndex = points.reduce(
                  (best, value, index) => (value != null ? index : best),
                  -1
                );
                return lastIndex >= 0
                  ? { entry, lastIndex, finalValue: points[lastIndex] }
                  : null;
              })
              .filter(Boolean);
            /* Direct-label only the top 3 by final value (legend carries the
               rest), with simple vertical collision avoidance. */
            const labeled = [...endpoints]
              .sort((a, b) => b.finalValue - a.finalValue)
              .slice(0, 3)
              .sort((a, b) => scale(a.finalValue) - scale(b.finalValue));
            const labelYByKey = new Map();
            let lastLabelY = -Infinity;
            labeled.forEach(({ entry, finalValue }) => {
              const y = Math.max(scale(finalValue) + 4, lastLabelY + 14);
              labelYByKey.set(entry.key, y);
              lastLabelY = y;
            });
            return endpoints.map(({ entry, lastIndex, finalValue }) => {
              const dimmed = highlightPlatform && highlightPlatform !== entry.key;
              return (
                <g key={entry.key} className={classNames(dimmed && "cpx-chart-segment--dimmed")}>
                  <path
                    className="cpx-chart-index-line"
                    stroke={entry.color}
                    d={linePath(entry.points || [], scale)}
                  />
                  <circle
                    cx={xCenter(lastIndex)}
                    cy={scale(finalValue)}
                    r={4}
                    fill={entry.color}
                    className="cpx-chart-index-endpoint"
                  />
                  {labelYByKey.has(entry.key) && (
                    <text
                      x={xCenter(lastIndex) + 8}
                      y={labelYByKey.get(entry.key)}
                      className="cpx-chart-index-label"
                    >
                      {entry.label}
                    </text>
                  )}
                </g>
              );
            });
          })()}
          {columns.map(
            (column, index) =>
              index % labelEvery === 0 && (
                <text
                  key={column.key}
                  x={xCenter(index)}
                  y={baseline + 22}
                  className="cpx-chart-axis-label"
                  textAnchor="middle"
                >
                  {column.label}
                </text>
              )
          )}
        </svg>
      )}

      {showEfficiencyPanel && (
        <svg
          className="cpx-chart-svg cpx-chart-svg--efficiency"
          width={width}
          height={linePanelHeight + 26}
          role="img"
          aria-label={`${efficiency.label || "Efficiency"} trend in its own panel. ${
            efficiency.definition || ""
          }`}
        >
          <text x={marginLeft - 10} y={12} className="cpx-chart-axis-label" textAnchor="end">
            {efficiency.label}
          </text>
          {[effMin, effMax].map((tick, tickIndex) => (
            <text
              key={`etick-${tickIndex}`}
              x={marginLeft - 10}
              y={effScale(tick) + 4}
              className="cpx-chart-axis-label"
              textAnchor="end"
            >
              {efficiency.format(tick)}
            </text>
          ))}
          {priorEffValues.length > 0 && (
            <path
              className="cpx-chart-eff-line cpx-chart-eff-line--prior"
              d={linePath(priorEfficiencySeries, effScale)}
            />
          )}
          <path className="cpx-chart-eff-line" d={linePath(efficiencySeries, effScale)} />
          {(() => {
            const lastIndex = efficiencySeries.reduce(
              (best, value, index) => (value != null ? index : best),
              -1
            );
            if (lastIndex < 0) return null;
            return (
              <g>
                <circle
                  cx={xCenter(lastIndex)}
                  cy={effScale(efficiencySeries[lastIndex])}
                  r={4}
                  className="cpx-chart-eff-endpoint"
                />
                <text
                  x={Math.min(xCenter(lastIndex) + 8, width - 8)}
                  y={effScale(efficiencySeries[lastIndex]) + 4}
                  className="cpx-chart-eff-endpoint-label"
                >
                  {efficiency.format(efficiencySeries[lastIndex])}
                </text>
              </g>
            );
          })()}
        </svg>
      )}

      {hoverColumn && renderTooltip(
        <div
          ref={tooltipRef}
          className={classNames(
            "cpx-chart-tooltip",
            floating && "cpx-chart-tooltip--floating",
            tooltipConstrained && "cpx-chart-tooltip--constrained"
          )}
          role="status"
          /* The chosen side, published so the placement contract can be
             asserted rather than eyeballed. */
          data-tooltip-placement={
            floating ? (tooltipPosition ? tooltipPosition.placement : "pending") : "inline"
          }
          style={floatingStyle}
          {...(tooltipConstrained ? { onPointerLeave: clearHover, onMouseLeave: clearHover } : {})}
        >
          <p className="cpx-chart-tooltip-title">{hoverColumn.label}</p>
          <ul className="cpx-chart-tooltip-list">
            {(hoverColumn.segments || []).map((segment) => {
              if (segment.value == null) return null;
              return (
                <li key={segment.key}>
                  <span
                    className="cpx-swatch"
                    style={{ backgroundColor: segment.color }}
                    aria-hidden="true"
                  />
                  <span className="cpx-chart-tooltip-name">{segment.label}</span>
                  <span className="cpx-chart-tooltip-value cpx-numeric">
                    {formatVolume(segment.value, { compact: true })}
                  </span>
                </li>
              );
            })}
            {showTotals && (
              <li className="cpx-chart-tooltip-total">
                <span className="cpx-chart-tooltip-name">Total</span>
                <span className="cpx-chart-tooltip-value cpx-numeric">
                  {formatVolume(columnTotal(hoverColumn), { compact: true })}
                </span>
              </li>
            )}
            {hoverColumn.planMedia > 0 && (
              <li>
                <span className="cpx-chart-tooltip-name">Plan</span>
                <span className="cpx-chart-tooltip-value cpx-numeric">
                  {formatPlan(hoverColumn.planMedia, { compact: true })}
                </span>
              </li>
            )}
            {/* Only on a date GA4 actually observed. The row used to read "No
                observation" on every other date, which spent a tooltip line
                restating what the broken line already says — and a reader
                scanning values sees an absent row as absent far faster than
                they read a sentence. The gap in the series and the spoken
                description are what carry "unreported, not zero". */}
            {showSiteVisits && hoverColumn.siteVisits != null && (
              <li className="cpx-chart-tooltip-site-visits">
                <span className="cpx-legend-line cpx-legend-line--visits" aria-hidden="true" />
                <span className="cpx-chart-tooltip-name">
                  {model.siteVisits?.label || "Site Visits · GA4"}
                </span>
                <span className="cpx-chart-tooltip-value cpx-numeric">
                  {formatSiteVisits(hoverColumn.siteVisits, { compact: false })}
                </span>
              </li>
            )}
            {efficiency && efficiencySeries[hoverIndex] != null && (
              <li>
                <span className="cpx-chart-tooltip-name">{efficiency.label}</span>
                <span className="cpx-chart-tooltip-value cpx-numeric">
                  {efficiency.format(efficiencySeries[hoverIndex])}
                </span>
              </li>
            )}
            {(hoverColumn.extraRows || []).map((row) => (
              <li key={`extra-${row.label}`}>
                <span className="cpx-chart-tooltip-name">{row.label}</span>
                <span className="cpx-chart-tooltip-value cpx-numeric">{row.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
